import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { RootState } from '../../store'
import imageService from '../../services/image.service'
import { addPreDefinedImage } from '../../views/LabelBuilder/LabelBuilder.slice'
import Card from '../Card'
import Icon from '../Icon'
import authService from '../../services/auth.service'
import PredefinedImages, { PredefinedImageResponse } from '../../apis/label.api'
import alertservice from '../../services/alert.service'
import { i18n } from '../../i18n/i18n'

interface Props {
}

type CombinedProps = Props & ConnectedProps<typeof connector>

const MAX_SIZE = 10 * 1024 * 1024

interface PredefinedImage {
    id: string
    src: string
    title: string
}

interface State {
    images: PredefinedImage[]
}

class ImagePicker extends Component<CombinedProps, State> {
  fileInputRef = React.createRef<HTMLInputElement>()

  state: State = {
    images: []
  }

  componentDidMount () {
    this.fetchPredefinedImages()
  }

  fetchPredefinedImages = () => {
    const authData = authService.tokenData()
    if (!authData) return

    const payload = { userId: authData.id }

    PredefinedImages.getPreDefinedImages(
      payload,
      (response) => {
        if (!response || response.data.length === 0) return

        const responseData: PredefinedImage[] = response.data.map(img => ({
          id: img.id.toString(),
          src: img.url,
          title: img.file_name
        }))

        this.setState({ images: responseData })
      }
    )
  }

  handleImageClick = (imageSrc: string) => {
    imageService.generateImage(imageSrc, (image) => {
      this.props.addPreDefinedImage(image)
    })
  }

  openImagePicker = () => {
    this.fileInputRef.current?.click()
  }

  formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
  }

  handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const image = e.target.files?.[0]
    if (!image) return

    const authData = authService.tokenData()
    if (!authData) return

    if (image.size > MAX_SIZE) {
      alertservice.error(
        i18n('file_size_too_big', {
          size: this.formatFileSize(image.size),
          max: '10 MB'
        })
      )
      return
    }

    const formData = new FormData()
    formData.append('image', image)
    formData.append('userId', authData.id?.toString())

    PredefinedImages.uploadPredefinedImage(formData, (response) => {
      if (response?.data) {
        const uploadedImage = {
          id: response.data.id.toString(),
          src: response.data.url,
          title: response.data.file_name
        }

        this.setState(prevState => ({
          images: [...prevState.images, uploadedImage]
        }))
        this.fetchPredefinedImages()
      }
    })
  }

  renderImageCard = (predefinedImage: PredefinedImage) => {
    return (
            <Card
                key={predefinedImage.id}
                background="darker"
                flat={true}
            >
                <div
                    className='justify-center items-center cursor-pointer text-center'
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      this.handleImageClick(predefinedImage.src)
                    }}
                >
                    <img
                        src={predefinedImage.src}
                        alt={predefinedImage.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                    />
                </div>
            </Card>
    )
  }

  render () {
    const { images } = this.state
    return (
            <div className='mb-4'>
                <div className="grid grid-cols-3 gap-4">
                    {images.map(image => this.renderImageCard(image))}
                    <Card
                        className="border-dashed border-2 border-gray-200 p-4 cursor-pointer"
                        background="darker"
                        onClick={this.openImagePicker}
                    >
                        <div
                            className="flex flex-col justify-center items-center text-center space-y-2"
                            style={{
                              width: '100%',
                              height: '100%'
                            }}
                        >
                            <Icon
                                name="file-image-line"
                                className="ri-file-image-line"
                                size="large"
                                color="gray"
                            />

                            <span
                                style={{
                                  fontSize: '12px',
                                  color: '#6B7280'
                                }}
                            >
                            {i18n('Click to Upload')}
                    </span>
                        </div>

                        <input
                            type="file"
                            accept="image/*"
                            ref={this.fileInputRef}
                            style={{ display: 'none' }}
                            onChange={this.handleImageUpload}
                        />
                    </Card>

                </div>
            </div>
    )
  }
}

function mapStateTopProps (state: RootState, props: Props) {
  return {}
}

const mapDispatchToProps = {
  addPreDefinedImage
}

const connector = connect(mapStateTopProps, mapDispatchToProps)
export default connector(ImagePicker)
